import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Copy,
  Check,
  Download,
  MessageCircle,
  Twitter,
  Facebook,
  Instagram,
  Share2,
  Loader2,
  Mail,
  Send,
  Linkedin,
  Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { API } from "@/App";

/**
 * ShareProfileDialog
 * Lets a star (or anyone) share a public star profile.
 * - Always shows a sparkle gradient avatar fallback when photo missing/fails
 * - Always renders the social share row (WhatsApp, Telegram, Twitter, Facebook,
 *   LinkedIn, Email, SMS, Instagram link, Copy)
 * - Bottom "Share" button uses Web Share API on mobile / falls back to copying
 *   the share message + URL on desktop.
 */
export default function ShareProfileDialog({ open, onClose, star }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const cardRef = useRef(null);

  if (!star) return null;

  // Build URLs
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const publicUrl = `${origin}/star/${star.user_id}`;
  // OG share URL — backend serves HTML with social meta tags so previews work
  const ogShareUrl = `${API}/share/star/${star.user_id}`;

  const shareMessage = `Check out ${star.name}'s profile on FoundStar! ✨`;
  const shareCombined = `${shareMessage}\n${ogShareUrl}`;

  const enc = encodeURIComponent;
  const socials = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      url: `https://wa.me/?text=${enc(shareCombined)}`,
      bg: "bg-[#25D366] hover:bg-[#1EBE5A]",
      Icon: MessageCircle,
    },
    {
      key: "telegram",
      label: "Telegram",
      url: `https://t.me/share/url?url=${enc(ogShareUrl)}&text=${enc(shareMessage)}`,
      bg: "bg-[#26A5E4] hover:bg-[#1E90C9]",
      Icon: Send,
    },
    {
      key: "twitter",
      label: "Twitter / X",
      url: `https://twitter.com/intent/tweet?text=${enc(shareMessage)}&url=${enc(ogShareUrl)}`,
      bg: "bg-black hover:bg-neutral-800",
      Icon: Twitter,
    },
    {
      key: "facebook",
      label: "Facebook",
      url: `https://www.facebook.com/sharer/sharer.php?u=${enc(ogShareUrl)}`,
      bg: "bg-[#1877F2] hover:bg-[#1565D8]",
      Icon: Facebook,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(ogShareUrl)}`,
      bg: "bg-[#0A66C2] hover:bg-[#085196]",
      Icon: Linkedin,
    },
    {
      key: "email",
      label: "Email",
      url: `mailto:?subject=${enc(`${star.name} on FoundStar`)}&body=${enc(shareCombined)}`,
      bg: "bg-[#64748B] hover:bg-[#475569]",
      Icon: Mail,
    },
    {
      key: "sms",
      label: "SMS",
      url: `sms:?&body=${enc(shareCombined)}`,
      bg: "bg-[#10B981] hover:bg-[#059669]",
      Icon: MessageCircle,
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // legacy fallback
      const ta = document.createElement("textarea");
      ta.value = publicUrl;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success("Link copied!");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Copy failed — long-press to copy manually");
      }
      document.body.removeChild(ta);
    }
  };

  const shareNative = async () => {
    const shareData = {
      title: `${star.name} on FoundStar`,
      text: shareMessage,
      url: ogShareUrl,
    };
    // On mobile / Mac Safari: open the OS-level share sheet (best UX)
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (e) {
        // User cancelled native share — don't show our menu either
        if (e && e.name === "AbortError") return;
        // Any other error → fall through to custom share menu
      }
    }
    // Desktop / unsupported browsers: open our custom share sheet
    setShareMenuOpen(true);
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });
      const link = document.createElement("a");
      link.download = `${(star.name || "star").replace(/\s+/g, "_")}-foundstar.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Profile card downloaded!");
    } catch (e) {
      console.error(e);
      toast.error("Download failed. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Resolve photo URL — accept absolute URLs, /api/files/... or bare keys
  const photoUrl = star.photo_url
    ? star.photo_url.startsWith("http")
      ? star.photo_url
      : star.photo_url.startsWith("/api/")
        ? `${API.replace("/api", "")}${star.photo_url.replace("/api", "")}`
        : `${API}/files/${star.photo_url}`
    : null;

  const showImage = photoUrl && !imgFailed;
  const categoryLabel = star.primary_category || (star.categories && star.categories[0]) || "Creator";
  const prettyCategory = (c) =>
    !c ? "" : String(c).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  const niches = Array.isArray(star.niches) ? star.niches.filter(Boolean) : [];
  const preciseLocation =
    star.location ||
    [star.village, star.city, star.district, star.state].filter(Boolean).join(", ");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1E293B]">
            <Share2 className="w-5 h-5 text-[#7B2FBE]" />
            Share Your Profile
          </DialogTitle>
        </DialogHeader>

        {/* Preview card (used for downloading too) */}
        <div className="flex justify-center py-2">
          <div
            ref={cardRef}
            className="relative w-[320px] rounded-2xl overflow-hidden shadow-lg"
            style={{
              background: "linear-gradient(135deg, #7B2FBE 0%, #FF69B4 100%)",
              padding: "24px",
            }}
          >
            <div className="text-center">
              <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-white shadow-xl relative bg-white/10">
                {showImage ? (
                  <img
                    src={photoUrl}
                    alt={star.name}
                    crossOrigin="anonymous"
                    className="w-full h-full object-cover"
                    onError={() => setImgFailed(true)}
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center relative"
                    style={{ background: "linear-gradient(135deg,#FF8FCF 0%,#C77DFF 50%,#7B2FBE 100%)" }}
                  >
                    <div
                      className="absolute inset-0"
                      style={{ background: "radial-gradient(circle at 30% 25%,rgba(255,255,255,0.45),transparent 55%)" }}
                    />
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="rgba(255,255,255,0.25)"
                      stroke="white"
                      strokeWidth="2.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="relative drop-shadow-md"
                    >
                      <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3z" />
                      <path d="M5 3v4" />
                      <path d="M19 17v4" />
                      <path d="M3 5h4" />
                      <path d="M17 19h4" />
                    </svg>
                  </div>
                )}
              </div>

              <h3 className="mt-3 font-outfit font-bold text-xl text-white flex items-center justify-center gap-1">
                {star.name}
                {star.is_verified && <span className="text-sm">✓</span>}
              </h3>

              {/* Categories — shown prominently as the role/profession from Star Profile */}
              {(() => {
                const cats = Array.isArray(star.categories) && star.categories.length > 0
                  ? star.categories
                  : (categoryLabel ? [categoryLabel] : []);
                const prettyCats = cats.map(prettyCategory).filter(Boolean);
                if (prettyCats.length === 0) return null;
                return (
                  <p className="text-white text-sm font-semibold mt-0.5 tracking-wide">
                    {prettyCats.slice(0, 3).join(" · ")}
                  </p>
                );
              })()}

              {preciseLocation && (
                <p className="text-white/80 text-[11px] mt-0.5">📍 {preciseLocation}</p>
              )}

              {/* Niches chips (different from categories — these are sub-interests) */}
              {niches.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1 justify-center">
                  {niches.slice(0, 5).map((n) => (
                    <span
                      key={`niche-${n}`}
                      className="text-[10px] font-semibold text-white bg-white/20 px-2 py-0.5 rounded-full backdrop-blur"
                    >
                      #{n}
                    </span>
                  ))}
                </div>
              )}

              {star.bio && (
                <p className="text-white/80 text-xs mt-2 line-clamp-2 italic">"{star.bio}"</p>
              )}

              {/* Stats */}
              {star.stats && (star.stats.followers > 0 || star.stats.rating > 0 || star.stats.completed_collabs > 0) && (
                <div className="flex items-center justify-center gap-3 mt-3 bg-white/15 backdrop-blur rounded-xl py-2 px-2">
                  {star.stats.followers > 0 && (
                    <div className="text-center px-2">
                      <p className="text-white font-bold text-sm">{formatCount(star.stats.followers)}</p>
                      <p className="text-white/70 text-[10px]">Followers</p>
                    </div>
                  )}
                  {star.stats.rating > 0 && (
                    <div className="text-center px-2 border-l border-white/30">
                      <p className="text-white font-bold text-sm">★ {star.stats.rating}</p>
                      <p className="text-white/70 text-[10px]">Rating</p>
                    </div>
                  )}
                  {star.stats.completed_collabs > 0 && (
                    <div className="text-center px-2 border-l border-white/30">
                      <p className="text-white font-bold text-sm">{star.stats.completed_collabs}</p>
                      <p className="text-white/70 text-[10px]">Collabs</p>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-white/20">
                <p className="text-white font-outfit font-bold text-sm">FoundStar</p>
                <p className="text-white/70 text-[10px]">Find · Book · Collaborate</p>
              </div>
            </div>
          </div>
        </div>

        {/* Copy Link */}
        <div className="mt-2">
          <label className="text-xs text-[#64748B] mb-1 block">Profile Link</label>
          <div className="flex gap-2">
            <Input value={publicUrl} readOnly onFocus={(e) => e.target.select()} className="text-xs" />
            <Button onClick={copyLink} variant="outline" size="icon" className="shrink-0" data-testid="copy-link-btn">
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Social Buttons */}
        <div className="mt-3">
          <p className="text-xs text-[#64748B] mb-2 font-medium">Share via</p>
          <div className="grid grid-cols-4 gap-2">
            {socials.map(({ key, label, url, bg, Icon }) => (
              <a
                key={key}
                href={url}
                target={key === "email" || key === "sms" ? "_self" : "_blank"}
                rel="noopener noreferrer"
                className={`flex flex-col items-center gap-1 py-3 rounded-lg ${bg} transition-colors text-white`}
                data-testid={`share-${key}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-semibold leading-tight">{label}</span>
              </a>
            ))}
            <button
              type="button"
              onClick={() => {
                copyLink();
                toast.info("Link copied! Paste it in your Instagram bio or story.");
              }}
              className="flex flex-col items-center gap-1 py-3 rounded-lg bg-gradient-to-br from-[#E1306C] via-[#C13584] to-[#833AB4] hover:opacity-90 transition-opacity text-white"
              data-testid="share-instagram"
            >
              <Instagram className="w-5 h-5" />
              <span className="text-[10px] font-semibold">Instagram</span>
            </button>
            <button
              type="button"
              onClick={copyLink}
              className="flex flex-col items-center gap-1 py-3 rounded-lg bg-gradient-to-br from-[#7B2FBE] to-[#FF69B4] hover:opacity-90 transition-opacity text-white"
              data-testid="share-copy"
            >
              <LinkIcon className="w-5 h-5" />
              <span className="text-[10px] font-semibold">Copy Link</span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 flex gap-2">
          <Button
            onClick={downloadCard}
            disabled={downloading}
            variant="outline"
            className="flex-1"
            data-testid="download-card-btn"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download Card
          </Button>
          <Button
            onClick={shareNative}
            className="flex-1 bg-gradient-to-r from-[#7B2FBE] to-[#FF69B4] text-white"
            data-testid="share-native-btn"
          >
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>

        <p className="text-[10px] text-[#94A3B8] text-center mt-2">
          Anyone with the link can view this profile and portfolio.
        </p>
      </DialogContent>

      {/* Secondary share-sheet popup — opens when user clicks the bottom Share
          button on a desktop / browser without Web Share API. Mirrors a native
          OS share sheet so the user can pick WhatsApp / SMS / Email / etc. */}
      <Dialog open={shareMenuOpen} onOpenChange={setShareMenuOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E293B]">
              <Share2 className="w-5 h-5 text-[#7B2FBE]" />
              Share to…
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#64748B] -mt-2 mb-1">Pick where to send {star.name}'s profile.</p>
          <div className="grid grid-cols-3 gap-3 py-2">
            {socials.map(({ key, label, url, bg, Icon }) => (
              <a
                key={key}
                href={url}
                target={key === "email" || key === "sms" ? "_self" : "_blank"}
                rel="noopener noreferrer"
                onClick={() => setTimeout(() => setShareMenuOpen(false), 300)}
                className={`flex flex-col items-center gap-1.5 py-4 rounded-xl ${bg} transition-colors text-white shadow-sm`}
                data-testid={`sheet-${key}`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-[11px] font-semibold leading-tight text-center">{label}</span>
              </a>
            ))}
            <button
              type="button"
              onClick={() => {
                copyLink();
                toast.info("Link copied! Paste in Instagram bio or story.");
                setShareMenuOpen(false);
              }}
              className="flex flex-col items-center gap-1.5 py-4 rounded-xl bg-gradient-to-br from-[#E1306C] via-[#C13584] to-[#833AB4] hover:opacity-90 text-white shadow-sm"
              data-testid="sheet-instagram"
            >
              <Instagram className="w-6 h-6" />
              <span className="text-[11px] font-semibold">Instagram</span>
            </button>
            <button
              type="button"
              onClick={() => { copyLink(); setShareMenuOpen(false); }}
              className="flex flex-col items-center gap-1.5 py-4 rounded-xl bg-gradient-to-br from-[#7B2FBE] to-[#FF69B4] hover:opacity-90 text-white shadow-sm"
              data-testid="sheet-copy"
            >
              <LinkIcon className="w-6 h-6" />
              <span className="text-[11px] font-semibold">Copy Link</span>
            </button>
          </div>
          <Button
            onClick={() => setShareMenuOpen(false)}
            variant="outline"
            className="w-full mt-1"
          >
            Cancel
          </Button>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function formatCount(n) {
  if (!n) return "0";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}
